'use client';

import {
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SavedQuery } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { useDAL } from '@/contexts/useDAL';

interface DuplicateSavedQueryDialogProps {
  savedQuery: SavedQuery;
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  onDuplicated: () => void;
}

export const DuplicateSavedQueryDialog = ({
  savedQuery,
  workspaceId,
  open,
  onClose,
  onDuplicated,
}: DuplicateSavedQueryDialogProps) => {
  const dal = useDAL();
  const [name, setName] = useState(`${savedQuery.name} (copy)`);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setName(`${savedQuery.name} (copy)`);
    setError(null);
    onClose();
  };

  const handleDuplicate = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await dal.lakehouse.createSavedQuery(workspaceId, {
        name: name.trim(),
        ...(savedQuery.description && {
          description: savedQuery.description,
        }),
        query: savedQuery.query,
        tags: [...(savedQuery.tags ?? [])],
      });
      onDuplicated();
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to duplicate saved query'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainDialog
      isOpen={open}
      isLoading={isLoading}
      header={{
        title: 'Duplicate Saved Query',
        onClose: handleClose,
      }}
      footer={{
        primaryButton: {
          text: 'Duplicate',
          onClick: handleDuplicate,
          'data-testid': 'duplicate-saved-query-btn',
          disabled: isLoading || !name.trim(),
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: handleClose,
          'data-testid': 'cancel-duplicate-saved-query-btn',
        },
      }}
    >
      <Stack gap='20px' padding='0 40px'>
        <Typography variant='body2' color='text.secondary'>
          Create a copy of &quot;{savedQuery.name}&quot; with a new name.
        </Typography>

        <FormControl fullWidth>
          <FormLabel>
            <Typography variant='subtitle1'>New Name *</Typography>
          </FormLabel>
          <TextField
            placeholder='Enter name for the copy'
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!error && !name.trim()}
            fullWidth
            size='small'
            autoFocus
          />
        </FormControl>

        {error && (
          <Typography color='error' variant='body2'>
            {error}
          </Typography>
        )}
      </Stack>
    </MainDialog>
  );
};
